import { Error } from "@progress/kendo-react-labels";
import { Window } from '@progress/kendo-react-dialogs';
import { DropDownList, DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";
import { useContext, useEffect, useState, cloneElement } from "react";
import { protectedResources } from "../authConfig";
import { useFetchWithMsal } from "../hooks/useFetchWithMsal";
import { IStreckContext } from "./PvsContextProvider";

interface StreckDropDownProps {
    context: React.Context<IStreckContext>,
    title: string,
    verb: string,
    endpoint: string,
    textField: string,
    selectedId?: number,
    selectedObject?: any,
    labId?: number,
    required: boolean,
    handleChange: ((selectedId: number) => void),
    addObjectElement?: React.ReactNode,
    disabled?: boolean,
    children?: React.ReactNode
}

export const StreckDropDown = (props: StreckDropDownProps) => {
    const [selected, setSelected] = useState({} as any);
    const [filteredData, setFilteredData] = useState(undefined as any[] | undefined);
    const context = useContext(props.context);

    const [showWindow, setShowWindow] = useState(false);

    const { execute } = useFetchWithMsal({
        scopes: protectedResources.scopes.admin
    });

    const getFilteredData = () => {
        if (props.labId) {
            setFilteredData(context.data?.filter(row => row.labId === props.labId));
        } else {
            setFilteredData(context.data);
        }
    }

    const selectFromFiltered = () => {
        if (props.selectedId && filteredData) {
            const found: any = filteredData.find((item: any) => item.id === props.selectedId);
            if (found) {
                setSelected(found);
            } else if (props.labId !== undefined && props.selectedObject?.labId === props.labId) {
                setSelected(props.selectedObject);
            } else {
                props.handleChange(0);
            } 
        }
    }

    const getDropDownData = () => {
        if (!context.data) {
            execute(props.verb, props.endpoint)
                .then(
                    ((result: any) => {
                        if (result) {
                            context.setData(result);
                            getFilteredData();
                        }
                    }),
                )
        } else {
            if (props.selectedId) {
                getFilteredData();
            }
        }
    }

    const listNoDataRender = (element: React.ReactElement<HTMLDivElement>) => {
        const noData = (
            <h4 style={{ fontSize: "1em" }}>
                <span className="k-icon k-i-warning" style={{ fontSize: "2.5em" }} />
                <br />
                <br />
                {props.addObjectElement ?
                    <>
                        No data.
                        Please add an item by clicking the <span className="k-icon k-i-plus-outline"></span> icon above.
                    </> :
                    <>No data. Please add an item.</>}
                
            </h4>
        );
        return cloneElement(element, { ...element.props }, noData);
    };

    const handleChange = (event: DropDownListChangeEvent) => {
        setSelected(event.target.value);
        props.handleChange(event.target.value.id);
    }

    const toggleAddClick = () => {
        setShowWindow(!showWindow);
    }

    const getStartingWindowWidth = () => {
        if (window.innerWidth > 700) {
            return 700;
        } else {
            return window.innerWidth;
        }
    }

    const getStartingWindowHeight = () => {
        if (window.innerHeight > 800) {
            return 800;
        } else {
            return window.innerHeight;
        }
    }

    useEffect(() => {
        selectFromFiltered();
    }, [filteredData]);

    useEffect(() => {
        getFilteredData();
    }, [props.labId, context.data]);

    useEffect(() => {
        getDropDownData();
    }, [execute]);

    return (<span style={{ maxWidth: "300px", display: "block", width: "100%" }} >
        <span>
            {'Select a' + props.title}{props.required && "*"} &nbsp;&nbsp;
            {props.addObjectElement &&
                <span title={'Add a' + props.title} className="k-icon k-i-plus-outline" onClick={toggleAddClick}></span>}
        </span>
        <DropDownList
            textField={props.textField}
            dataItemKey="id"
            data={filteredData}
            value={selected}
            listNoDataRender={listNoDataRender}
            onChange={handleChange}
            disabled={!(props.labId == null || props.labId > 0) || props.disabled}
        ></DropDownList>
        {props.required && !(selected?.id) &&
            <Error>This is a required field</Error>}
        {showWindow && (
            <Window title={'Add a' + props.title} onClose={toggleAddClick} initialWidth={getStartingWindowWidth()} initialHeight={getStartingWindowHeight()}>
                {props.addObjectElement}
            </Window>)}
    </span>);
}